import { useEffect, useMemo } from 'react';

import {
  ChakraBox,
  ChakraSimpleGrid,
  ChakraVStack,
  DateFormat,
  DatePickerInput,
  DropdownOption,
  DropdownV2,
  InputGroupV2,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  BILLING_PERIODS_TOP_LEVEL_ID,
  DATE_PLACEHOLDER,
} from '~/lib/constants';
import { OTHER_PAYOR } from '~/lib/constants/inquiry.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  extractExternalIds,
  getTreatmentEpisodeDescription,
} from '~/lib/util/billingPeriod.util';
import util from '~/lib/util/util';
import {
  useGetEquipPayorsQuery,
  useGetPatientCoveragesQuery,
  useGetPatientTreatmentEpisodesQuery,
  useGetPayorProgramsQuery,
} from '~/store/services/clinicalData';

import { BillingPeriodCoverageValues } from './types';
import { selectActiveTreatmentEpisodeProgram } from '~/components/maud/patients/treatmentEpisode/util';

interface BillingPeriodDetailsProps {
  billingPeriodDetails: BillingPeriodCoverageValues;
  showRequiredFieldErrors: boolean;
  isReadOnly?: boolean;
  updateBillingPeriodState?: (val: Partial<BillingPeriod>) => void;
}

const BillingPeriodDetails = ({
  billingPeriodDetails,
  isReadOnly,
  showRequiredFieldErrors,
  updateBillingPeriodState,
}: BillingPeriodDetailsProps) => {
  const {
    treatmentEpisode,
    payor,
    equipPayor,
    payorProgramName,
    endDate,
    startDate,
  } = billingPeriodDetails;

  const { currentPatientId } = useGlobalContext();
  const { data: patientCoveragesData } = useGetPatientCoveragesQuery(
    { patientId: currentPatientId },
    {
      skip: !currentPatientId,
    },
  );

  const patientCoverages = useMemo(() => {
    return patientCoveragesData?.map((obj) => ({
      coverageExternalId: obj.coverageExternalId,
      externalId: obj.payorExternalId,
      label: obj.payor === OTHER_PAYOR ? obj.customPayorName : obj.payor,
    }));
  }, [patientCoveragesData]);

  useEffect(() => {
    if (patientCoverages && !isReadOnly) {
      const currentCoverage = patientCoverages.find((coverage) => {
        return coverage.label?.toLowerCase() === payor?.toLowerCase();
      });

      updateBillingPeriodState({
        payorExternalId: currentCoverage?.externalId,
      });
    }
  }, [isReadOnly, patientCoverages, payor, updateBillingPeriodState]);

  const { data: treatmentEpisodesData } = useGetPatientTreatmentEpisodesQuery(
    currentPatientId,
    { skip: isReadOnly || !currentPatientId },
  );

  const { data: equipPayorsData } = useGetEquipPayorsQuery(
    billingPeriodDetails.payorExternalId,
    {
      skip: isReadOnly || !billingPeriodDetails.payorExternalId,
    },
  );

  const { data: payorPrograms } = useGetPayorProgramsQuery(
    billingPeriodDetails.equipPayorId,
    {
      skip: !equipPayor || isReadOnly,
    },
  );

  const equipPayors = useMemo(() => {
    return equipPayorsData?.map((obj) => ({
      externalId: obj.equipPayorId,
      label: obj.name,
    }));
  }, [equipPayorsData]);

  const treatmentEpisodes = useMemo(() => {
    const episodes = treatmentEpisodesData?.data ?? [];

    return episodes.map((episode) => {
      const programs = episode.treatmentPrograms ?? [];
      if (!programs.length) {
        return {
          externalId: episode.treatmentEpisodeId,
          label: getTreatmentEpisodeDescription(
            episode.treatmentProgramName,
            episode.admitDate,
          ),
        };
      }

      const activeProgram = selectActiveTreatmentEpisodeProgram(programs);
      return {
        externalId: episode.treatmentEpisodeId,
        label: getTreatmentEpisodeDescription(
          activeProgram.programName,
          activeProgram.startDate,
        ),
      };
    });
  }, [treatmentEpisodesData]);

  const equipPayorMenOptions: DropdownOption[] = useMemo(() => {
    // Upon clearing the payor dropdown clear the equip payor  dropdown as well
    return billingPeriodDetails.payorExternalId
      ? (equipPayors ?? []).map(({ label }) => ({
          label,
          value: label,
        }))
      : null;
  }, [billingPeriodDetails.payorExternalId, equipPayors]);

  const payorProgramMenuOptions: DropdownOption[] = useMemo(() => {
    // Upon clearing the equip payor dropdown clear the payor programs dropdown as well
    return billingPeriodDetails.equipPayorId
      ? (payorPrograms ?? []).map(({ label }) => ({
          label,
          value: label,
        }))
      : null;
  }, [billingPeriodDetails.equipPayorId, payorPrograms]);

  const treatmentEpisodeRender = useMemo(() => {
    return (
      <InputGroupV2
        id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-treatment-episode`}
        isRequired={isReadOnly || false}
        label="Treatment episode"
      >
        <DropdownV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-treatment-episode-dropdown`}
          isInvalid={showRequiredFieldErrors}
          isReadOnly={isReadOnly}
          menuOptions={(treatmentEpisodes ?? []).map(({ label }) => ({
            label,
            value: label,
          }))}
          onOptionClick={(value: string) => {
            const splitName = value?.split(',');

            updateBillingPeriodState({
              episodeStartDate: splitName[1]?.trim(),
              treatmentEpisodeId: extractExternalIds(treatmentEpisodes, value)
                ?.externalId,
              treatmentProgramName: splitName[0]?.trim(),
            });
          }}
          placeholder="Please choose"
          value={treatmentEpisode}
        />
      </InputGroupV2>
    );
  }, [
    isReadOnly,
    showRequiredFieldErrors,
    treatmentEpisode,
    treatmentEpisodes,
    updateBillingPeriodState,
  ]);

  const payorRenderer = useMemo(() => {
    return (
      <InputGroupV2
        id={`${BILLING_PERIODS_TOP_LEVEL_ID}__payors`}
        isRequired
        label="Insurance plan"
      >
        <DropdownV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__payors-dropdown`}
          isInvalid={showRequiredFieldErrors}
          isReadOnly={isReadOnly}
          menuOptions={(patientCoverages ?? []).map(({ label }) => ({
            label,
            value: label,
          }))}
          onOptionClick={(value: string) => {
            const ids = extractExternalIds(patientCoverages, value);
            updateBillingPeriodState({
              coverageExternalId: ids?.coverageExternalId,
              customPayorName: null,
              equipPayorExternalId: null,
              equipPayorName: null,
              payorExternalId: ids?.externalId,
              payorName: value,
              payorProgramExternalId: null,
              payorProgramName: null,
            });
          }}
          placeholder="Please choose"
          value={util.capitalize(payor)}
        />
      </InputGroupV2>
    );
  }, [
    showRequiredFieldErrors,
    isReadOnly,
    patientCoverages,
    payor,
    updateBillingPeriodState,
  ]);

  const equipPayorRenderer = useMemo(() => {
    return (
      <InputGroupV2
        id={`${BILLING_PERIODS_TOP_LEVEL_ID}__equip-payor`}
        isRequired
        label="Equip payor"
      >
        <DropdownV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__equip-payor-dropdown`}
          isInvalid={showRequiredFieldErrors}
          isReadOnly={isReadOnly}
          menuOptions={equipPayorMenOptions}
          onOptionClick={(value: string) => {
            updateBillingPeriodState({
              equipPayorExternalId: extractExternalIds(equipPayors, value)
                ?.externalId,
              equipPayorName: value,
              payorProgramExternalId: null,
              payorProgramName: null,
            });
          }}
          placeholder="Please choose"
          value={util.capitalize(equipPayor)}
        />
      </InputGroupV2>
    );
  }, [
    showRequiredFieldErrors,
    isReadOnly,
    equipPayorMenOptions,
    equipPayor,
    updateBillingPeriodState,
    equipPayors,
  ]);

  const payorProgramRenderer = useMemo(() => {
    return (
      <InputGroupV2
        id={`${BILLING_PERIODS_TOP_LEVEL_ID}__payor-program`}
        isRequired
        label="Payor program"
      >
        <DropdownV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__payor-program-dropdown`}
          isInvalid={showRequiredFieldErrors}
          isReadOnly={isReadOnly}
          menuOptions={payorProgramMenuOptions}
          onOptionClick={(value: string) =>
            updateBillingPeriodState({
              payorProgramExternalId: extractExternalIds(payorPrograms, value)
                ?.externalId,
              payorProgramName: value,
            })
          }
          placeholder="Please choose"
          value={util.capitalize(payorProgramName)}
        />
      </InputGroupV2>
    );
  }, [
    showRequiredFieldErrors,
    isReadOnly,
    payorProgramMenuOptions,
    payorProgramName,
    updateBillingPeriodState,
    payorPrograms,
  ]);

  return (
    <ChakraBox id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details`} width="100%">
      <ChakraVStack alignItems="flex-stretch" gap="24px">
        {treatmentEpisodeRender}
        <ChakraSimpleGrid columns={3} spacing="20px" width="100%">
          {payorRenderer}
          {equipPayorRenderer}
          {payorProgramRenderer}
        </ChakraSimpleGrid>

        <ChakraSimpleGrid
          columns={isReadOnly ? 3 : 2}
          spacing="20px"
          width="100%"
        >
          <InputGroupV2
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-startdate`}
            label="Start date"
          >
            <DatePickerInput
              defaultDate={
                startDate
                  ? DateTime.fromFormat(
                      startDate,
                      DateFormat.shortYearMonthDay,
                    ).toJSDate()
                  : undefined
              }
              id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-startdate-input`}
              isReadOnly={isReadOnly}
              onSubmit={(date: Date) => {
                updateBillingPeriodState({
                  startDate: DateTime.fromJSDate(date).toISODate(),
                });
              }}
              placeholder={DATE_PLACEHOLDER}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-enddate`}
            isRequired={isReadOnly || false}
            label="End date"
          >
            <DatePickerInput
              defaultDate={
                endDate
                  ? DateTime.fromFormat(
                      endDate,
                      DateFormat.shortYearMonthDay,
                    ).toJSDate()
                  : undefined
              }
              id={`${BILLING_PERIODS_TOP_LEVEL_ID}__details-enddate-input`}
              isReadOnly={isReadOnly}
              minDate={new Date(startDate)}
              onSubmit={(date: Date) => {
                updateBillingPeriodState({
                  endDate: DateTime.fromJSDate(date).toISODate(),
                });
              }}
              placeholder={DATE_PLACEHOLDER}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
      </ChakraVStack>
    </ChakraBox>
  );
};

export default BillingPeriodDetails;
